/**
 * googleAdsTracking.js
 * V37.44.45 — Centralized Google Ads tag + conversion tracking.
 *
 * Why this module exists:
 *   - A single source of truth for every conversion label we fire. New
 *     events are added by registering an entry in CONVERSION_EVENTS;
 *     call sites never hard-code `send_to` strings or sessionStorage
 *     guard keys.
 *   - Per-event idempotency (sessionStorage) prevents double-fire across
 *     React StrictMode double invocations, primary + Safari-fallback
 *     OAuth handlers, and SPA back/forward navigations.
 *   - Honors Google Consent Mode v2. Events only fire when
 *     `ad_user_data` / `ad_personalization` consent is 'granted' (or
 *     when consent mode is not wired up — permissive default to match
 *     prior behaviour; tighten in the future by setting
 *     `requireExplicitConsent = true`).
 *   - Fails-open on tracking errors — `fire()` NEVER throws into
 *     application code. Auth, generation, payment flows must not be
 *     interrupted by analytics plumbing.
 *
 * How to add a new conversion event:
 *   1. Append an entry to CONVERSION_EVENTS below with:
 *        key              : stable short id, used as sessionStorage key
 *        label            : the Google Ads conversion label
 *        description      : human comment for maintainers
 *        allowMultiFire   : set true for repeatable events (e.g. prompt
 *                           generation). Defaults to false (one-shot).
 *   2. Call `fireConversion('your-key', { value, currency, ... })`
 *      wherever the event occurs.
 *
 * This module purposely does NOT re-load gtag.js. The script tag in
 * `public/index.html` is the single tag installation; this module
 * only pushes events through the global `window.gtag`.
 */

import devLog from './devLog';

// --- Config ----------------------------------------------------------------

export const GOOGLE_ADS_CONVERSION_ID = 'AW-18193869031';

/**
 * Registered conversion events.
 * NEW events: add here, call `fireConversion(key)` from the originating code path.
 */
export const CONVERSION_EVENTS = Object.freeze({
  signup: {
    label: 'XO2hCJ-AytocEOfRweND',
    description: 'Fires exactly once after a genuine new-user Google OAuth signup.',
    allowMultiFire: false,
  },
  // Example scaffolding for future events — uncomment + fill label as needed:
  // first_prompt_generated: {
  //   label: 'REPLACE_WITH_CONVERSION_LABEL',
  //   description: 'User completes their first prompt generation.',
  //   allowMultiFire: false,
  // },
  // purchase: {
  //   label: 'REPLACE_WITH_CONVERSION_LABEL',
  //   description: 'Successful paid plan upgrade.',
  //   allowMultiFire: true,  // can repeat per transaction
  // },
});

// If you later turn on cookie consent prompts, flip this to `true` so
// events are blocked until the user explicitly grants ad consent.
const requireExplicitConsent = false;

// --- Internal helpers ------------------------------------------------------

function _sessionStorageSafe() {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch (_) {
    return null;
  }
}

function _storageKey(key) {
  return `ep_gads_fired_${key}`;
}

/**
 * Resolve the user's current Google Consent Mode v2 state.
 * Returns true if ads tracking is permitted. In the absence of a consent
 * prompt (current app state) we default to permissive to preserve
 * historical tracking behaviour — flip `requireExplicitConsent` to true
 * once a cookie banner is wired up.
 */
function _consentGranted() {
  if (typeof window === 'undefined') return false;
  const consent = window.__gads_consent;
  if (!consent) return !requireExplicitConsent;
  return (
    consent.ad_user_data === 'granted' &&
    consent.ad_personalization === 'granted'
  );
}

/**
 * Primitive gtag accessor. If gtag.js is still loading, the inline shim
 * (`window.gtag = window.gtag || function(){ window.dataLayer.push(...) }`
 * declared in `public/index.html`) queues the call for later processing.
 */
function _gtag() {
  if (typeof window === 'undefined') return null;
  if (typeof window.gtag === 'function') return window.gtag;
  window.dataLayer = window.dataLayer || [];
  return function queued() { window.dataLayer.push(arguments); };
}

// --- Public API ------------------------------------------------------------

/**
 * Fire a configured Google Ads conversion event.
 *
 * @param {string} key — matches a key in CONVERSION_EVENTS
 * @param {object} [params] — optional extra fields (value, currency, transaction_id, …)
 * @param {object} [opts]
 * @param {string} [opts.source] — free-form string recorded in dev logs
 * @returns {boolean} true if the event was dispatched; false if guarded / blocked
 */
export function fireConversion(key, params = {}, opts = {}) {
  try {
    const event = CONVERSION_EVENTS[key];
    if (!event) {
      devLog.warn(`[gads] Unknown conversion key: ${key}`);
      return false;
    }

    if (!_consentGranted()) {
      devLog.log(`[gads] Conversion '${key}' blocked by consent state`);
      return false;
    }

    const storage = _sessionStorageSafe();
    const storageKey = _storageKey(key);
    if (!event.allowMultiFire && storage) {
      if (storage.getItem(storageKey) === '1') {
        devLog.log(`[gads] Conversion '${key}' already fired this session — skipping`);
        return false;
      }
    }

    const gtagFn = _gtag();
    if (!gtagFn) {
      devLog.warn(`[gads] gtag unavailable for '${key}'`);
      return false;
    }

    gtagFn('event', 'conversion', {
      send_to: `${GOOGLE_ADS_CONVERSION_ID}/${event.label}`,
      transport_type: 'beacon',
      event_callback: function () {
        try {
          devLog.log(`[gads] '${key}' beacon ack (source=${opts.source || 'direct'})`);
        } catch (_) { /* no-op */ }
      },
      ...params,
    });

    if (!event.allowMultiFire && storage) {
      try { storage.setItem(storageKey, '1'); } catch (_) { /* no-op */ }
    }

    devLog.log(`[gads] fired '${key}' (source=${opts.source || 'direct'})`);
    return true;
  } catch (e) {
    // Fails-open: tracking must never break app flow.
    devLog.warn(`[gads] fire('${key}') threw:`, e);
    return false;
  }
}

/**
 * Public helper to update Google Consent Mode v2 state at runtime.
 * Call from your cookie-banner UI when the user accepts / rejects.
 *
 * @param {{ad_storage?: string, ad_user_data?: string, ad_personalization?: string, analytics_storage?: string}} consent
 */
export function updateAdsConsent(consent) {
  try {
    if (typeof window === 'undefined') return;
    window.__gads_consent = { ...(window.__gads_consent || {}), ...consent };
    const gtagFn = _gtag();
    if (gtagFn) gtagFn('consent', 'update', consent);
  } catch (e) {
    devLog.warn('[gads] updateAdsConsent threw:', e);
  }
}

/**
 * Convenience: inspect which conversions have fired in this tab session.
 * Returns an object: { [eventKey]: '1' | null }.
 */
export function debugFiredState() {
  const storage = _sessionStorageSafe();
  const out = {};
  for (const key of Object.keys(CONVERSION_EVENTS)) {
    out[key] = storage ? storage.getItem(_storageKey(key)) : null;
  }
  return out;
}

export default { fireConversion, updateAdsConsent, debugFiredState, CONVERSION_EVENTS };
